import {Viewer, Worker, SpecialZoomLevel} from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import {defaultLayoutPlugin} from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import {useState, useEffect, useRef} from "react";
import {get} from "lodash";
import './PdfViewer.scss';

/**
 * PDF Viewer component with mobile-optimized single-page navigation
 * @param {string} url - URL of the PDF file to display
 * @param {Function} onReadingCompleted - Callback fired when user reaches the last page
 */
export const PdfViewer = ({url, onReadingCompleted}) => {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const viewerRef = useRef(null);

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Check if fullscreen API is supported
    const supportsFullscreen = document.fullscreenEnabled ||
                               document.webkitFullscreenEnabled ||
                               document.mozFullScreenEnabled ||
                               document.msFullscreenEnabled;

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) =>  [],
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        ZoomOut,
                        ZoomIn,
                        EnterFullScreen,
                        GoToPreviousPage,
                        GoToNextPage,
                        CurrentPageLabel,
                        NumberOfPages
                    } = slots;

                    return (
                        <div className="PdfViewer__toolbar-container">
                            <div className="PdfViewer__toolbar-controls">
                                {isMobile && (
                                    <>
                                        <GoToPreviousPage />
                                        <GoToNextPage />
                                    </>
                                )}
                                <ZoomOut />
                                <ZoomIn />
                                {supportsFullscreen && <EnterFullScreen />}
                            </div>
                            <div className="PdfViewer__toolbar-page-info">
                                <CurrentPageLabel />
                                <span>/</span>
                                <NumberOfPages />
                            </div>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });
    
    // Force a resize event when entering/exiting full screen to ensure the viewer scales correctly
    useEffect(() => {
        const handleFullScreenChange = () => {
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 100);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
        document.addEventListener('mozfullscreenchange', handleFullScreenChange);
        document.addEventListener('MSFullscreenChange', handleFullScreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
        };
    }, []);

    /**
     * Handles document load event
     * @param {Object} params - Document load parameters
     * @param {Object} params.doc - PDF document object
     */
    const handleDocumentLoad = ({doc}) => {
        const numPages = get(doc, '_pdfInfo.numPages')
        setNumPages(numPages);
        if(numPages === 1 && onReadingCompleted){
            onReadingCompleted();
        }
    }

    /**
     * Handles page change event
     * @param {Object} params - Page change parameters
     * @param {number} params.currentPage - Current page index (0-based)
     */
    const handlePageChange = ({ currentPage }) => {
        setCurrentPage(currentPage);

        // Trigger completion when reaching the last page
        if (currentPage === numPages - 1 && onReadingCompleted) {
            onReadingCompleted();
        }
    }

    return (
        <div
            ref={viewerRef}
            className={`PdfViewer ${isMobile ? 'PdfViewer--mobile' : ''}`}
        >
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.js">
                <Viewer
                    onDocumentLoad={handleDocumentLoad}
                    onPageChange={handlePageChange}
                    fileUrl={url}
                    // This line is for PT medium vulnerability of pdfjs-dist
                    transformGetDocumentParams={options => Object.assign({}, options, { isEvalSupported: false, }) }
                    plugins={[
                        defaultLayoutPluginInstance,
                    ]}
                    // Mobile: PageWidth for readability, Desktop: PageWidth for consistency
                    defaultScale={SpecialZoomLevel.PageWidth}
                />
            </Worker>
        </div>
    );
}